<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="promptlevel-editor q-pa-sm">
      <q-card-section class="row">
        <div
          v-if="isReadOnly"
          class="text-h6"
        >
          {{ $t('programs.promptLevels.editPromptLevelTemplate.textTitlePreview') }}
        </div>
        <div
          v-else-if="hasPromptLevelTemplateId"
          class="text-h6"
        >
          {{ $t('programs.promptLevels.editPromptLevelTemplate.textTitleEdit') }}
        </div>
        <div
          v-else
          class="text-h6"
        >
          {{ $t('programs.promptLevels.editPromptLevelTemplate.textTitleAdd') }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-form @submit="onSavePromptLevelTemplate">
        <q-card-section class="q-pt-none">
          <div class="row col-12 items-center">
            <q-input
              ref="templateName"
              v-model="editPromptLevelTemplate.templateName"
              outlined
              hide-bottom-space
              :readonly="isReadOnly"
              :rules="[
                val =>
                  !!val || $t(
                    'programs.promptLevels.editPromptLevelTemplate.inputTemplateNameValidator'
                  )
              ]"
              :label="$t('programs.promptLevels.editPromptLevelTemplate.inputTemplateNameLabel')"
              class="col q-pr-sm"
            />
            <div
              v-if="!isReadOnly"
              class="col-auto"
            >
              <q-btn
                icon="settings"
                color="dark-variant"
                flat
                round
                dense
              >
                <q-menu>
                  <div
                    class="q-pa-xs text-center text-subtitle2 bg-primary text-white"
                  >
                    {{ $t('programs.promptLevels.editPromptLevelTemplate.textAdvancedSettingsTitle') }}
                  </div>
                  <q-separator />
                  <q-list>
                    <q-item
                      v-ripple
                      clickable
                    >
                      <q-item-section avatar>
                        <q-icon
                          name="scale"
                          color="dark-variant"
                        />
                      </q-item-section>
                      <q-item-section
                        style="white-space:nowrap"
                      >
                        {{ $t('programs.promptLevels.editPromptLevelTemplate.listItemMeasurement') }}
                      </q-item-section>
                      <q-item-section side>
                        <q-icon name="keyboard_arrow_right" />
                      </q-item-section>
                      <q-menu>
                        <q-item
                          v-ripple
                          v-close-popup
                          clickable
                          :active="outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.BINARY"
                          @click="onSetOutcomeMeasurement(OUTCOME_MEASUREMENT_TYPES.BINARY)"
                        >
                          <q-item-section avatar>
                            <q-icon
                              name="toggle_on"
                              color="dark-variant"
                            />
                          </q-item-section>
                          <q-item-section>
                            {{ $t('programs.promptLevels.editPromptLevelTemplate.listItemMeasurementBinary') }}
                          </q-item-section>
                        </q-item>
                        <q-item
                          v-ripple
                          v-close-popup
                          clickable
                          :active="outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE"
                          @click="onSetOutcomeMeasurement(OUTCOME_MEASUREMENT_TYPES.RATING_SCALE)"
                        >
                          <q-item-section avatar>
                            <q-icon
                              name="123"
                              color="dark-variant"
                            />
                          </q-item-section>
                          <q-item-section>
                            {{ $t('programs.promptLevels.editPromptLevelTemplate.listItemMeasurementRatingScale') }}
                          </q-item-section>
                        </q-item>
                        <q-item
                          v-ripple
                          v-close-popup
                          clickable
                          :active="outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.WEIGHTED"
                          @click="onSetOutcomeMeasurement(OUTCOME_MEASUREMENT_TYPES.WEIGHTED)"
                        >
                          <q-item-section avatar>
                            <q-icon
                              name="linear_scale"
                              color="dark-variant"
                            />
                          </q-item-section>
                          <q-item-section>
                            {{ $t('programs.promptLevels.editPromptLevelTemplate.listItemMeasurementWeighted') }}
                          </q-item-section>
                        </q-item>
                      </q-menu>
                    </q-item>
                    <q-item
                      v-ripple
                      clickable
                      :disable="outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE"
                    >
                      <q-item-section avatar>
                        <q-icon
                          name="live_help"
                          color="dark-variant"
                        />
                      </q-item-section>
                      <q-item-section
                        style="white-space:nowrap"
                      >
                        {{ $t('programs.promptLevels.editPromptLevelTemplate.promptLevelFadingLabel') }}
                      </q-item-section>
                      <q-item-section side>
                        <q-icon name="keyboard_arrow_right" />
                      </q-item-section>
                      <q-menu v-if="outcomeMeasurement !== OUTCOME_MEASUREMENT_TYPES.RATING_SCALE">
                        <q-item
                          v-for="fadingStrategy in fadingStrategies"
                          :key="fadingStrategy.value"
                          v-ripple
                          v-close-popup
                          clickable
                          :active="editPromptLevelTemplate.fadingStrategy === fadingStrategy.value"
                          @click="onSetFadingStrategy(fadingStrategy.value)"
                        >
                          <q-item-section>
                            {{ fadingStrategy.label }}
                          </q-item-section>
                        </q-item>
                      </q-menu>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
          </div>
        </q-card-section>
        <q-card-section v-if="hasPromptLevelFadingStrategy">
          <q-card>
            <q-card-section>
              <p class="text-overline text-grey-6 text-uppercase q-ma-none">
                {{ $t('programs.promptLevels.editPromptLevelTemplate.mostToLeastHeading') }}
              </p>
              <p class="text-subtitle2">
                {{ $t('programs.promptLevels.editPromptLevelTemplate.mostToLeastHint') }}
              </p>
              <q-select
                v-model="editPromptLevelTemplate.initialPromptLevelType"
                :readonly="isReadOnly"
                :label="$t('programs.promptLevels.editPromptLevelTemplate.initialPromptLevelLabel')"
                align="right"
                :options="initialPromptLevelTypes"
                map-options
                emit-value
              />
              <q-input
                v-if="editPromptLevelTemplate.initialPromptLevelType === 'mass_trial_probe'"
                v-model.number="editPromptLevelTemplate.initialMassTrialThreshold"
                type="number"
                :readonly="isReadOnly"
                :label="$t('programs.promptLevels.editPromptLevelTemplate.initialMassTrialThresholdLabel')"
                :suffix="$t('programs.promptLevels.editPromptLevelTemplate.initialMassTrialThresholdSuffix')"
                align="right"
                class="col-6"
                input-class="text-right"
              />
              <div class="row items-center q-col-gutter-md">
                <q-input
                  v-model.number="editPromptLevelTemplate.fadingDecreasePercentage"
                  type="number"
                  :readonly="isReadOnly"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.fadingDecreasePercentageLabel')"
                  :suffix="$t('programs.promptLevels.editPromptLevelTemplate.fadingDecreasePercentageSuffix')"
                  align="right"
                  class="col-6"
                  input-class="text-right"
                />
                <q-input
                  v-if="hasMTLPromptLevelFadingStrategy"
                  v-model.number="editPromptLevelTemplate.fadingDecreaseTrials"
                  type="number"
                  :readonly="isReadOnly"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.fadingDecreaseTrialsLabel')"
                  :suffix="$t('programs.promptLevels.editPromptLevelTemplate.fadingTrialsSuffix')"
                  class="col-6"
                  input-class="text-right"
                />
                <q-input
                  v-else-if="hasMTLSPromptLevelFadingStrategy"
                  v-model.number="editPromptLevelTemplate.fadingDecreaseSessions"
                  type="number"
                  :readonly="isReadOnly"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.fadingDecreaseTrialsLabel')"
                  :suffix="$t('programs.promptLevels.editPromptLevelTemplate.fadingSessionsSuffix')"
                  class="col-6"
                  input-class="text-right"
                />
              </div>
              <div class="row items-center q-col-gutter-md">
                <q-input
                  v-model.number="editPromptLevelTemplate.fadingIncreasePercentage"
                  type="number"
                  :readonly="isReadOnly"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.fadingIncreasePercentageLabel')"
                  :suffix="$t('programs.promptLevels.editPromptLevelTemplate.fadingIncreasePercentageSuffix')"
                  align="right"
                  class="col-6"
                  input-class="text-right"
                />
                <q-input
                  v-if="hasMTLPromptLevelFadingStrategy"
                  v-model.number="editPromptLevelTemplate.fadingIncreaseTrials"
                  type="number"
                  :readonly="isReadOnly"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.fadingIncreaseTrialsLabel')"
                  :suffix="$t('programs.promptLevels.editPromptLevelTemplate.fadingTrialsSuffix')"
                  class="col-6"
                  input-class="text-right"
                />
                <q-input
                  v-else-if="hasMTLSPromptLevelFadingStrategy"
                  v-model.number="editPromptLevelTemplate.fadingIncreaseSessions"
                  type="number"
                  :readonly="isReadOnly"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.fadingIncreaseTrialsLabel')"
                  :suffix="$t('programs.promptLevels.editPromptLevelTemplate.fadingSessionsSuffix')"
                  class="col-6"
                  input-class="text-right"
                />
              </div>
              <div class="q-mt-sm col-12">
                <q-checkbox
                  :model-value="simpleFading"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.checkBoxSimpleFading')"
                  :disable="isReadOnly"
                  @update:model-value="onSetSimpleFading"
                />
              </div>
              <div
                v-if="simpleFading"
                class="q-mt-sm col-12"
              >
                <q-input
                  v-model="simpleFadingPromptLevelLabel"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.inputSimpleFadingPromptLevelLabel')"
                  :readonly="isReadOnly"
                />
              </div>
            </q-card-section>
          </q-card>
        </q-card-section>
        <q-card-section>
          <q-card
            v-for="(promptLevel, key) in visibleOrderedPromptLevels"
            :key="promptLevel.order"
            class="row q-pa-sm q-mb-md"
          >
            <div class="col row">
              <q-card-section class="col-xs-12 col-sm-6 row">
                <q-btn
                  icon="colorize"
                  size="sm"
                  :disable="isReadOnly"
                  class="col-auto q-mr-sm"
                  :style="getPromptLevelStyles(promptLevel)"
                >
                  <q-popup-proxy
                    ref="colorPicker"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-color
                      :model-value="promptLevel.color"
                      no-header
                      default-view="palette"
                      @update:model-value="onChangeColor($event, key, promptLevel)"
                      @change="hideColorPicker($event, key)"
                    />
                  </q-popup-proxy>
                </q-btn>
                <q-input
                  ref="promptLevel"
                  v-model="promptLevel.trialOutcome"
                  :readonly="isReadOnly"
                  :label="$t('programs.promptLevels.editPromptLevelTemplate.inputPromptLevelLabel')"
                  :rules="[
                    val =>
                      !!val || $t('programs.promptLevels.editPromptLevelTemplate.inputPromptNameValidator')
                  ]"
                  class="col"
                  @update:model-value="onPromptLevelChange(promptLevel)"
                  @keydown.enter.prevent="onAddPromptLevel"
                />
              </q-card-section>
              <q-card-section class="col-xs-12 col-sm-6">
                <q-input
                  v-if="isNumericOutcome"
                  :model-value="promptLevel.score"
                  type="number"
                  min="0"
                  :max="outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.WEIGHTED ? 100 : null"
                  outlined
                  clearable
                  hide-bottom-space
                  :label="scoreInputLabel"
                  :readonly="isReadOnly"
                  :rules="[scoreValueRule]"
                  input-class="text-right"
                  @update:model-value="onWeightedScoreValueChange($event, promptLevel)"
                />
                <q-toggle
                  v-else
                  v-model="promptLevel.score"
                  checked-icon="check"
                  :disable="isReadOnly"
                  :false-value="0"
                  :true-value="100"
                  color="positive"
                  :label="$t(
                    'programs.promptLevels.editPromptLevelTemplate.inputSuccessfulOutcomeLabel'
                  )"
                  unchecked-icon="clear"
                  @update:model-value="onPromptLevelChange(promptLevel)"
                >
                  <q-tooltip v-if="!isReadOnly">
                    {{
                      $t('programs.promptLevels.editPromptLevelTemplate.tooltipSuccessfulOutcome')
                    }}
                  </q-tooltip>
                </q-toggle>
                <q-toggle
                  v-if="hasPromptLevelFadingStrategy"
                  v-model="promptLevel.excludeFromPromptLevelFading"
                  :disable="isReadOnly"
                  checked-icon="not_interested"
                  color="negative"
                  :label="$t(
                    'programs.promptLevels.editPromptLevelTemplate.inputExcludeFromFadingLabel'
                  )"
                  @update:model-value="onPromptLevelChange(promptLevel)"
                >
                  <q-tooltip>
                    {{
                      $t('programs.promptLevels.editPromptLevelTemplate.tooltipExcludeFromFading')
                    }}
                  </q-tooltip>
                </q-toggle>
              </q-card-section>
            </div>
            <q-card-section
              class="col-auto q-ma-none q-pa-none column row justify-center"
            >
              <div class="q-pb-xs">
                <q-btn
                  icon="keyboard_arrow_up"
                  :disable="isReadOnly || (promptLevel.order === 1)"
                  size="sm"
                  flat
                  round
                  @click="onReorderPromptLevel(promptLevel, -1)"
                />
              </div>
              <div full-width>
                <q-separator />
              </div>
              <div class="q-pt-xs">
                <q-btn
                  icon="keyboard_arrow_down"
                  :disable="isReadOnly || (promptLevel.order === orderedPromptLevels.length)"
                  size="sm"
                  flat
                  round
                  @click="onReorderPromptLevel(promptLevel, +1)"
                />
              </div>
            </q-card-section>
            <q-card-section
              class="col-auto q-ma-none q-pa-none column row text-center justify-center"
            >
              <div>
                <q-btn
                  icon="delete"
                  color="negative"
                  :disable="isReadOnly"
                  size="sm"
                  flat
                  round
                  class="q-ml-sm"
                  @click="onDeletePromptLevel(promptLevel)"
                />
              </div>
            </q-card-section>
          </q-card>
          <q-card
            v-ripple
            class="row q-pa-sm"
            :class="{ 'cursor-pointer': !isReadOnly }"
            @click="onAddPromptLevel"
          >
            <q-card-section class="row col-12 justify-center ">
              <q-btn
                flat
                :disable="isReadOnly"
                color="primary"
                class="col-auto"
                @click.stop="onAddPromptLevel"
              >
                <span>
                  {{ $t('programs.promptLevels.editPromptLevelTemplate.buttonAddPromptLevel') }}
                </span>
                <span
                  class="rounded-borders q-ml-md q-py-xs q-px-lg"
                  :class="{ hidden: $q.platform.is.mobile }"
                  style="border:solid lightgray 1px"
                >&#9166;</span>
              </q-btn>
            </q-card-section>
          </q-card>
        </q-card-section>
        <q-card-section
          v-if="allowDefault"
        >
          <q-toggle
            v-model="editPromptLevelTemplate.isDefault"
            :disable="isReadOnly"
            checked-icon="check"
            :false-value="false"
            :true-value="true"
            color="green"
            :label="$t('programs.promptLevels.editPromptLevelTemplate.toggleUseAsDefault')"
            unchecked-icon="clear"
          >
            <q-tooltip>
              {{ $t('programs.promptLevels.editPromptLevelTemplate.tooltipUseAsDefault') }}
            </q-tooltip>
          </q-toggle>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            v-if="isReadOnly"
            v-close-popup
            flat
            :label="$t('common.buttonClose')"
            color="primary"
          />
          <template
            v-else
          >
            <q-btn
              v-close-popup
              flat
              :label="$t('common.buttonCancel')"
              color="secondary"
            />
            <q-btn
              flat
              :label="$t('common.buttonSave')"
              type="submit"
              color="primary"
              data-e2e="button-save"
              :disable="isSaveDisabled"
              :loading="savingSpinner"
            />
          </template>
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>
<script>
import {
  chain, cloneDeep, findIndex, get, isEmpty, omit, reject, size, toNumber,
} from 'lodash';
import { mapState } from 'pinia';
import Targets from 'src/mixins/Targets';
import { auth } from 'src/store/auth';
import { OUTCOME_MEASUREMENT_TYPES } from 'src/constants/prompt-level-template';
import { isNumericOutcome, isRatingScale } from 'src/utils/PromptLevelUtils';

export default {
  name: 'EditPromptLevelTemplate',

  mixins: [Targets],

  props: {
    promptLevelTemplate: {
      type: Object,
      required: true,
    },
    clinic: {
      type: String,
      default: null,
    },
    package: {
      type: String,
      default: null,
    },
    allowDefault: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      savingSpinner: false,

      editPromptLevelTemplate: {
        id: null,
        clinic: null,
        package: null,
        templateName: '',
        fadingStrategy: '',
        outcomeMeasurement: '',
        simpleFading: false,
      },
      editPromptLevels: [],

      fadingStrategies: [
        {
          value: '',
          label: this.$t('programs.promptLevels.editPromptLevelTemplate.promptLevelFadingStrategies.none'),
        },
        {
          value: 'MTL',
          label: this.$t('programs.promptLevels.editPromptLevelTemplate.promptLevelFadingStrategies.mtl'),
        },
        {
          value: 'MTL-S',
          label: this.$t('programs.promptLevels.editPromptLevelTemplate.promptLevelFadingStrategies.mtl-sessions'),
        },
        /*        {
          value: 'LTM',
          label: this.$t('programs.promptLevels.editPromptLevelTemplate.promptLevelFadingStrategies.ltm'),
        }, */
      ],

      initialPromptLevelTypes: [
        {
          value: 'highest',
          label: this.$t('programs.promptLevels.editPromptLevelTemplate.initialPromptLevelTypes.highest'),
        },
        {
          value: 'last_probe',
          label: this.$t('programs.promptLevels.editPromptLevelTemplate.initialPromptLevelTypes.last_probe'),
        },
        {
          value: 'mass_trial_probe',
          label: this.$t('programs.promptLevels.editPromptLevelTemplate.initialPromptLevelTypes.mass_trial_probe'),
        },
      ],
    };
  },

  computed: {
    ...mapState(auth, ['hasTherapistCredentials']),

    OUTCOME_MEASUREMENT_TYPES() {
      return OUTCOME_MEASUREMENT_TYPES;
    },

    userCanEditPromptLevelTemplate() {
      return this.hasTherapistCredentials;
    }, // userCanEditPromptLevelTemplate()

    isReadOnly() {
      return this.readonly || (!this.userCanEditPromptLevelTemplate);
    }, // isReadOnly()

    hasPromptLevelTemplateId() {
      return (get(this.editPromptLevelTemplate, 'id', null) !== null);
    }, // hasPromptLevelTemplateId()

    hasMTLPromptLevelFadingStrategy() {
      return get(this.editPromptLevelTemplate, 'fadingStrategy', '') === 'MTL';
    },

    hasMTLSPromptLevelFadingStrategy() {
      return get(this.editPromptLevelTemplate, 'fadingStrategy', '') === 'MTL-S';
    },

    hasPromptLevelFadingStrategy() {
      if (this.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE) {
        return false;
      }
      return this.hasMTLPromptLevelFadingStrategy
        || this.hasMTLSPromptLevelFadingStrategy;
    },

    isSaveDisabled() {
      return size(this.orderedPromptLevels) === 0;
    }, // isSaveDisabled()

    orderedPromptLevels() {
      if (isEmpty(this.editPromptLevels)) {
        return [];
      }
      return chain(this.editPromptLevels)
        .reject({ editStatus: 'delete' })
        .sortBy('order')
        .value();
    },

    hideExcludedPromptLevels() {
      return this.hasPromptLevelFadingStrategy && this.simpleFading;
    }, // hideExcludedPromptLevels()

    /**
     * If we are using simple prompt fading we should not show
     * prompt levels that are marked as excluded from prompt level fading
     * (since they are only used to indicate a negative response).
     */
    visibleOrderedPromptLevels() {
      if (!this.hideExcludedPromptLevels) {
        return this.orderedPromptLevels;
      }
      return this.orderedPromptLevels
        .filter((pl) => !pl.isNegativeFadeResponse);
    }, // visibleOrderedPromptLevels()

    outcomeMeasurement: {
      get() {
        return get(this.editPromptLevelTemplate, 'outcomeMeasurement', OUTCOME_MEASUREMENT_TYPES.BINARY);
      },
      set(v) {
        this.editPromptLevelTemplate.outcomeMeasurement = v;

        if (v === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE) {
          let currentScore = 1;
          this.editPromptLevels = this.editPromptLevels.map((pl) => {
            if (pl.editStatus === 'delete') { return pl; }

            const updatedPl = {
              ...pl,
              score: currentScore,
            };

            if (updatedPl.editStatus !== 'create') {
              updatedPl.editStatus = 'update';
            }

            currentScore += 1;
            return updatedPl;
          });
        }
      },
    },

    simpleFading: {
      get() {
        return get(this.editPromptLevelTemplate, 'simpleFading', false);
      },
      set(v) {
        this.editPromptLevelTemplate.simpleFading = v;
      },
    },

    /**
     * Simple prompt level fading (+/-) will use the first prompt level
     * that is indicated as negative response and is excluded from prompt fading
     */
    simpleFadingPromptLevel() {
      const simpleFadingPromptLevelIdx = this.orderedPromptLevels.findIndex(
        (pl) => pl.excludeFromPromptLevelFading && pl.isNegativeFadeResponse,
      );
      if (simpleFadingPromptLevelIdx === -1) {
        return null;
      }
      return this.orderedPromptLevels[simpleFadingPromptLevelIdx];
    }, // simpleFadingPromptLevel()

    simpleFadingPromptLevelLabel: {
      get() {
        return this.simpleFadingPromptLevel?.trialOutcome ?? '-';
      },
      set(v) {
        if (this.simpleFadingPromptLevel) {
          this.simpleFadingPromptLevel.trialOutcome = v;
          this.onPromptLevelChange(this.simpleFadingPromptLevel, undefined, null);
        } else {
          this.addPromptLevel({
            trialOutcome: '-',
            score: 0,
            excludeFromPromptLevelFading: true,
            isNegativeFadeResponse: true,
          }, true);
        }
      },
    },

    scoreInputLabel() {
      return isRatingScale(this.outcomeMeasurement)
        ? this.$t('programs.promptLevels.editPromptLevelTemplate.inputRatingValueLabel')
        : this.$t('programs.promptLevels.editPromptLevelTemplate.inputScoreLabel');
    }, // scoreInputLabel()

    isNumericOutcome() {
      return isNumericOutcome(this.outcomeMeasurement);
    }, // isNumericOutcome()

  },

  watch: {
    promptLevelTemplate: {
      immediate: true,
      deep: false,
      handler(newPromptLevelTemplate) {
        if (isEmpty(newPromptLevelTemplate)) {
          this.editPromptLevelTemplate = {
            id: null,
            clinic: this.clinic,
            package: this.package,

            fadingStrategy: '',
            initialPromptLevelType: 'highest',
            outcomeMeasurement: OUTCOME_MEASUREMENT_TYPES.BINARY,
            isDefault: false,
            isDeleted: false,
            isLocked: false,
          };
          this.editPromptLevels = [];
        } else {
          this.editPromptLevelTemplate = { ...omit(newPromptLevelTemplate, 'promptLevels') };
          this.editPromptLevels = cloneDeep(get(newPromptLevelTemplate, 'promptLevels', []));
        }
      },
    },
  },

  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.initSimpleFadingPromptLevelLabel();
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    hideColorPicker(event, key) {
      const colorPicker = get(this.$refs.colorPicker, key, null);

      if (colorPicker) {
        colorPicker.hide();
      }
    },

    onChangeColor(color, key, promptLevel) {
      this.onPromptLevelChange(promptLevel, undefined, color);
    }, // onChangeColor()

    onSetOutcomeMeasurement(outcomeMeasurement) {
      if (this.outcomeMeasurement === outcomeMeasurement) {
        return;
      }
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.promptLevels.editPromptLevelTemplate.dialogConfirmPromptLevelTypeChange'),
          cancel: true,
          persistent: true,
        })
        .onOk(() => {
          this.outcomeMeasurement = outcomeMeasurement;
        });
    }, // onSetOutcomeMeasurement()

    onSetFadingStrategy(fadingStrategy) {
      this.editPromptLevelTemplate = {
        ...this.editPromptLevelTemplate,
        fadingDecreaseSessions: null,
        fadingDecreaseTrials: null,
        fadingIncreaseSessions: null,
        fadingIncreaseTrials: null,
        fadingStrategy,
      };
    }, // onSetFadingStrategy()

    onSetSimpleFading(val) {
      if (this.readonly) {
        return;
      }
      if (val && !this.simpleFading) {
        // Add simple fading prompt level
        this.simpleFadingPromptLevelLabel = '-';
      } else if (!val && this.simpleFading) {
        // Remove simple fading prompt level
        this.deletePromptLevel(this.simpleFadingPromptLevel);
      }
      this.simpleFading = val;
    }, // onSetSimpleFading()

    onAddPromptLevel() {
      if (this.isReadOnly) {
        return;
      }
      this.addPromptLevel();
    }, // onAddPromptLevel()

    onDeletePromptLevel(promptLevel) {
      if (this.isReadOnly) {
        return;
      }
      if (promptLevel.editStatus === 'create') {
        this.deletePromptLevel(promptLevel);
        return;
      }

      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.promptLevels.editPromptLevelTemplate.dialogDeletePromptLevelMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(() => {
          this.deletePromptLevel(promptLevel);
        });
    },

    onReorderPromptLevel(promptLevel, direction) {
      const newPromptLevel = chain(this.editPromptLevels)
        .reject({ editStatus: 'delete' })
        .find({ order: promptLevel.order })
        .value();
      const swapPromptLevel = chain(this.editPromptLevels)
        .reject({ editStatus: 'delete' })
        .find({ order: promptLevel.order + direction })
        .value();

      swapPromptLevel.order = newPromptLevel.order;
      newPromptLevel.order += direction;
      this.onPromptLevelChange(swapPromptLevel);
      this.onPromptLevelChange(newPromptLevel);
    },

    /**
     * Vue has an issue with the input type number: https://github.com/vuejs/core/issues/3378
     * if the value of the input was deleted (e.g. using backspace) we need to handle an empty string here
     */
    onWeightedScoreValueChange(value, promptLevel) {
      const scoreValue = (value === '' || value === null)
        ? null
        : toNumber(value);
      this.onPromptLevelChange(promptLevel, scoreValue);
    }, // onWeightedScoreValueChange()

    onPromptLevelChange(promptLevel, scoreValue, color = null) {
      const newPromptLevelIndex = findIndex(this.editPromptLevels, promptLevel);
      if (newPromptLevelIndex === -1) {
        return;
      }

      const updatedPromptLevel = cloneDeep(this.editPromptLevels[newPromptLevelIndex]);

      if (typeof scoreValue !== 'undefined') {
        updatedPromptLevel.score = scoreValue;
      }

      if (color) {
        updatedPromptLevel.color = color;
      }

      updatedPromptLevel.editStatus = updatedPromptLevel.editStatus === 'create'
        ? 'create'
        : 'update';

      this.editPromptLevels[newPromptLevelIndex] = updatedPromptLevel;
    }, // onPromptLevelChange()

    async onSavePromptLevelTemplate() {
      const cleanedTemplateData = this.cleanFadingDataForRatingScale(this.editPromptLevelTemplate);
      const returnPromptLevelTemplate = {
        ...cleanedTemplateData,
        promptLevels: this.editPromptLevels,
      };
      this.$emit('ok', returnPromptLevelTemplate);
      this.hide();
    }, // onSavePromptLevelTemplate()

    /**
     * Initialize the simple fading prompt level label
     * If simple fading is enabled, we need to ensure that the simple fading prompt level label is set.
     * We do this for backwards compatibility reasons, as the simple fading prompt level label
     * was not always set when simple fading was enabled.
     */
    initSimpleFadingPromptLevelLabel() {
      if (this.hasPromptLevelFadingStrategy && this.simpleFading) {
        // If we have simple fading enabled, we need to ensure that the simple fading prompt level label is set
        if (!this.simpleFadingPromptLevel) {
          this.simpleFadingPromptLevelLabel = '-';
        }
      }
    },

    addPromptLevel(data = {}, preventFocus = false) {
      const index = chain(this.orderedPromptLevels)
        .maxBy('order')
        .get('order')
        .defaultTo(0)
        .value();

      // Auto-increment rating: default to 1, or next value in sequence
      let defaultScore = 0;
      if (this.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE) {
        const visibleLevels = this.visibleOrderedPromptLevels;
        if (visibleLevels.length > 0) {
          const lastItemScore = visibleLevels[visibleLevels.length - 1].score;
          defaultScore = (lastItemScore === null || lastItemScore === '') ? 1 : toNumber(lastItemScore) + 1;
        } else {
          defaultScore = 1;
        }
      }

      this.editPromptLevels.push({
        trialOutcome: '',
        score: defaultScore,
        excludeFromPromptLevelFading: false,
        ...data,
        editStatus: 'create',
        order: index + 1,
        isDeleted: false,
      });
      if (preventFocus) {
        return;
      }
      this.$nextTick(() => {
        const input = this.$refs.promptLevel[this.visibleOrderedPromptLevels.length - 1];
        if (input) {
          input.focus();
        }
      });
    },

    deletePromptLevel(promptLevel) {
      if (promptLevel.editStatus === 'create') {
        // Never saved before - just remove it from array that will be saved later
        this.editPromptLevels = reject(
          this.editPromptLevels,
          promptLevel,
        );
      } else {
        // Mark for deletion in the database
        chain(this.editPromptLevels)
          .find(promptLevel)
          .set('editStatus', 'delete')
          .value();
        // newPromptLevel.editStatus = 'delete';
      }

      let order = 1;
      // Update order indexes to adjust for deleted item
      this.editPromptLevels = chain(
        this.editPromptLevels,
      )
        .orderBy('order')
        .map(
          (promptLevelObj) => {
            if (get(promptLevelObj, 'editStatus') === 'delete') {
              return promptLevelObj;
            }
            const retVal = {
              ...promptLevelObj,
              order,
            };
            if (promptLevelObj.order !== order) {
              retVal.editStatus = (promptLevelObj.editStatus === 'create') ? 'create' : 'update';
            }
            order += 1;
            return retVal;
          },
        ).value();
    }, // deletePrompt()

    getPromptLevelStyles(promptLevel) {
      const color = this.getPromptLevelTextColor(promptLevel);
      const bgColor = this.getPromptLevelColor(promptLevel);
      return `color:${color}; background-color:${bgColor}`;
    },

    scoreValueRule(val) {
      if (val === null || val === undefined || val === '') {
        return this.$t('programs.promptLevels.editPromptLevelTemplate.inputScoreValidator');
      }
      return true;
    }, // scoreValueRule()

    cleanFadingDataForRatingScale(templateData) {
      const isExistingRatingScaleWithFading = this.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE
        && !!templateData.fadingStrategy;

      if (isExistingRatingScaleWithFading) {
        return {
          ...templateData,
          fadingStrategy: '',
          initialPromptLevelType: 'highest',
          initialMassTrialThreshold: null,
          fadingDecreasePercentage: null,
          fadingDecreaseTrials: null,
          fadingDecreaseSessions: null,
          fadingIncreasePercentage: null,
          fadingIncreaseTrials: null,
          fadingIncreaseSessions: null,
          simpleFading: false,
        };
      }

      return templateData;
    },

  },

};
</script>
<style scoped>
.promptlevel-editor {
  min-width:75vw;
}
</style>
